// Client-specific configuration for the Spaulding & Oakes delegated-document-
// work demo — the same templatization seam as the other demos' scenarios.
// Components read from this object and never hard-code client names or
// narrative text.
//
// Spaulding & Oakes leads with a distinct LAYOUT — a matter-file timeline
// (MatterTimeline) whose steps are the four actions of ONE sealed execution
// record — with the live seal behind a second tab, sharing the same LivePanel +
// gig catalog. A toggle switches between two recorded receipts: a conformant
// filing (N.D. Cal.) and one filed in a court off the mandate's allowlist
// (E.D. Tex.).

import type { LivePanelCopy } from "@demo/ui/LivePanel.js";
import type { MatterTimelineConfig } from "@demo/ui/MatterTimeline.js";

export interface SpauldingOakesScenario {
  client: { name: string; domain: string; mark: string };
  product: { name: string; tagline: string };
  disclosure: string;
  masthead: string;
  intro: { headline: string; body: string };
  // Overview tab: the matter-file timeline. Scenario bundleIds key into
  // LEGAL_BUNDLES (apps/legal-demo/src/data.ts).
  timeline: MatterTimelineConfig;
  liveCta: { label: string; sub: string };
  live: LivePanelCopy;
}

const CONFORMANT_STEPS = [
  {
    icon: "draft" as const,
    step: "Step 1 · drafted",
    title: "Motion drafted",
    detail:
      "Drafted from the matter file. The document text is hashed into the receipt, never sealed raw."
  },
  {
    icon: "cite" as const,
    step: "Step 2 · cite-checked",
    title: "Citations verified",
    detail:
      "The agent records how many authorities it cite-checked, bound to the exact drafted document by hash."
  },
  {
    icon: "file" as const,
    step: "Step 3 · filed",
    title: "Filed with N.D. Cal.",
    detail: "Court is on the mandate's allowlist: the filing conforms."
  },
  {
    icon: "docket" as const,
    step: "Step 4 · docketed",
    title: "Docket confirmation recorded",
    detail: "The court's docket number is bound into the receipt chain, tied to the filing."
  }
];

const VIOLATION_STEPS = [
  CONFORMANT_STEPS[0],
  CONFORMANT_STEPS[1],
  {
    icon: "file" as const,
    step: "Step 3 · filed",
    title: "Filed with E.D. Tex.",
    detail: "Court is NOT on the mandate's allowlist: the filing breaches the mandate."
  },
  CONFORMANT_STEPS[3]
];

export const SPAULDING_OAKES: SpauldingOakesScenario = {
  client: {
    name: "Spaulding & Oakes LLP",
    domain: "spauldingoakes.com",
    mark: "SO"
  },
  product: {
    name: "Sequesign",
    tagline: "Verifiable receipts for agent actions"
  },
  disclosure:
    "Fictional demonstration. Spaulding & Oakes, the matter, and the filing shown are not real. The document text, matter reference, and title are hashed into the receipt, never sealed raw: the receipt carries no privileged content. Each execution record is a real Sequesign receipt, sealed by an in-process demo witness and verified live in your browser.",
  masthead: "Delegated work, on the record",
  timeline: {
    eyebrow: "See it in 15 seconds",
    headline: "An agent drafted and filed this motion. Trust the file, or verify the record.",
    sub: "A drafting agent took a matter from draft to docket under a filing mandate. Each step is a sealed action; together they're the matter's execution record. The document text is only hashed, never sealed. Step through it, then verify the whole record in your browser:",
    matterTitle: "Matter 2026-0417: Motion to Compel",
    matterSub: "mandate document_execution · matter ref sealed as a hash",
    mandate: {
      docTypesLabel: "Allowed docs",
      docTypes: "motion · brief · notice",
      courtsLabel: "Allowed courts",
      courts: "N.D. Cal. · 9th Cir."
    },
    scenarios: [
      {
        key: "conformant",
        toggleLabel: "Conformant filing",
        bundleId: "filing-conformant",
        steps: CONFORMANT_STEPS,
        execTitle: "Execution record: conformant filing",
        execDetail:
          "Four sealed steps, bound end to end. Verify the record to confirm every signature, hash, and the bound mandate: the filing went to an allowed court.",
        expectConformant: true
      },
      {
        key: "violation",
        toggleLabel: "Out-of-mandate filing",
        bundleId: "filing-violation",
        steps: VIOLATION_STEPS,
        execTitle: "Execution record: out-of-mandate filing",
        execDetail:
          "The same four sealed steps, but the filing went to a court off the mandate's allowlist. Verify the record: it's still authentic, and it honestly records conformant: false.",
        expectConformant: false
      }
    ],
    verifyLabel: "Verify the execution record",
    verifyingLabel: "Verifying…",
    reverifyLabel: "Verify again",
    browserNote:
      "The whole chain is one receipt: verifying it checks every step together. Switch to the out-of-mandate filing to see the same record when the agent files in a court off the allowlist: still authentic, honestly recording conformant: false."
  },
  liveCta: {
    label: "Run a real seal with your own keys",
    sub: "Presenter mode: seal a brand-new execution record against the real witness, end to end, and verify it here."
  },
  intro: {
    headline: "Every delegated filing, on the record.",
    body: "Spaulding & Oakes's drafting agent takes a matter from draft to docket under a filing mandate: the allowed document types and the allowed courts. Enter the matter and run it: the agent seals a real Sequesign execution record server-side (drafted → cite-checked → filed → docketed), then you verify it right here in your browser. File in a court off the allowlist, or a document type off the allowlist, and the sealed record honestly records conformant: false. It still verifies as authentic; the breach is recorded, not hidden. The document text, matter reference, and title are only hashed, never sealed."
  },
  live: {
    title: "Live run: real seal, real witness",
    intro: [
      "Enter the matter and run it: the agent executes a real Sequesign session with your own agent key, seals the four-step execution record, and gets each step countersigned by a real witness, then verifies it here in your browser.",
      "The result is verified in EXTERNAL trust-anchor mode: the witness's published keys are fetched from its well-known endpoint (directly, or relayed unchanged through this site) and used as the trust anchor, so this is an independent third-party check, not an integrity self-check.",
      "The filing mandate binds the allowed document types (motion, brief, notice) and the allowed courts (N.D. Cal., 9th Cir.). File in a court off the allowlist (e.g. E.D. Tex.), or pick the off-allowlist “discovery_request” document type, and the sealed record honestly records conformant: false. It still verifies as authentic; the breach is recorded, not hidden. The document text, matter reference, and title are hashed into the receipt, never sealed raw."
    ],
    contactNote:
      "Live mode contacts the real witness using the keys you enter below. Nothing else on this page makes a network call to that service.",
    keyNote:
      "Keys are used only for this run, in your browser and the request; nothing is stored. They live in React state only, are never written to localStorage, and are cleared when you leave the page.",
    runLabel: "Seal the execution record",
    runningLabel: "Sealing…",
    fields: {
      agentPem: "Agent private key (Ed25519 PEM)",
      witnessBaseUrl: "Witness base URL",
      witnessApiKey: "Witness API key (optional)",
      llmApiKey: "Anthropic API key"
    }
  }
};
